import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FiMail, FiArrowLeft, FiSend, FiCheckCircle, FiAlertCircle } from "react-icons/fi";

// -------- Glass surfaces (shared with the rest of the redesigned app) -----

const glassPanel = {
  background:
    "linear-gradient(135deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.08) 55%, rgba(255,255,255,0.15) 100%)",
  backdropFilter: "blur(22px) saturate(108%)",
  WebkitBackdropFilter: "blur(22px) saturate(108%)",
  border: "1px solid rgba(255,255,255,0.45)",
  boxShadow:
    "0 18px 44px rgba(55,79,99,0.13), inset 0 1px 0 rgba(255,255,255,0.55)",
};

const isValidEmail = (email) => /\S+@\S+\.\S+/.test(email);

// A single ambiguous confirmation is intentionally shown for both success
// and 404 so we don't leak which emails are registered.
const GENERIC_SENT_MESSAGE =
  "If an account exists for that email, a reset link is on its way. Check your inbox.";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [errorText, setErrorText] = useState("");

  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem("bibleMemosTheme") || "powder";
    } catch (_e) {
      return "powder";
    }
  });
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "bibleMemosTheme" && e.newValue) setTheme(e.newValue);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const submit = async (event) => {
    event.preventDefault();
    if (!isValidEmail(email)) {
      setStatus("error");
      setErrorText("Please enter a valid email address.");
      return;
    }
    setStatus("sending");
    setErrorText("");
    try {
      const response = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      // Treat 200 and 404 the same on the client so we don't reveal whether
      // the email exists in our system. Any other error surface a generic
      // "please try again" message.
      if (response.ok || response.status === 404) {
        setStatus("sent");
      } else {
        setStatus("error");
        setErrorText("We couldn't send the reset link. Please try again in a moment.");
      }
    } catch (_e) {
      setStatus("error");
      setErrorText("Network error. Please check your connection and try again.");
    }
  };

  return (
    <div
      data-theme={theme}
      className="min-h-screen bg-page-bg text-on-surface font-inter transition-colors overflow-x-hidden"
    >
      <main className="w-full">
        <div className="w-full max-w-[520px] mx-auto px-margin-mobile py-space-xl flex flex-col gap-space-lg">
          <div
            className="rounded-[1.5rem] p-space-lg md:p-space-xl flex flex-col gap-space-md"
            style={glassPanel}
          >
            <div className="flex flex-col gap-space-xs">
              <span className="text-label-sm uppercase tracking-widest text-accent-gold-deep font-bold">
                Account · Recovery
              </span>
              <h1 className="font-noto-serif text-headline-lg text-neu-primary">
                Forgot your password?
              </h1>
              <p className="text-body-md text-on-surface-variant">
                Enter the email you signed up with and we'll send a link to set a new one.
              </p>
            </div>

            {status === "sent" ? (
              <div
                className="rounded-[1rem] p-space-sm flex items-start gap-2 text-body-md text-neu-primary"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(255,255,255,0.55), rgba(255,255,255,0.35))",
                  border: "1px solid rgba(255,255,255,0.45)",
                }}
                aria-live="polite"
              >
                <FiCheckCircle className="text-accent-cta mt-0.5 shrink-0" />
                <span>{GENERIC_SENT_MESSAGE}</span>
              </div>
            ) : (
              <form onSubmit={submit} className="flex flex-col gap-space-md" noValidate>
                <label className="flex flex-col gap-1">
                  <span className="text-label-sm uppercase tracking-wider text-on-surface-variant font-semibold">
                    Email
                  </span>
                  <div className="relative">
                    <FiMail
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-on-surface-variant"
                      aria-hidden="true"
                    />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="email"
                      required
                      placeholder="you@example.com"
                      className="w-full h-12 pl-11 pr-4 rounded-full bg-white/70 border border-white/60 text-body-md text-neu-primary placeholder:text-on-surface-variant focus:outline-none focus:bg-white/90 transition-colors"
                    />
                  </div>
                </label>

                {status === "error" && errorText && (
                  <div
                    role="alert"
                    className="flex items-start gap-2 text-label-md text-[#b02a37]"
                  >
                    <FiAlertCircle className="mt-0.5 shrink-0" />
                    <span>{errorText}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="inline-flex items-center justify-center gap-2 min-h-[48px] w-full rounded-full bg-accent-cta text-white text-label-md font-semibold shadow-[-3px_-3px_7px_var(--outer-shadow-light),4px_5px_10px_rgba(0,0,0,0.22)] hover:bg-accent-cta-hover active:shadow-[inset_2px_2px_5px_rgba(0,0,0,0.3),inset_-2px_-2px_5px_rgba(255,255,255,0.2)] disabled:opacity-60 disabled:cursor-not-allowed transition-all"
                >
                  <FiSend aria-hidden="true" />
                  {status === "sending" ? "Sending…" : "Send Reset Link"}
                </button>
              </form>
            )}

            <div className="pt-space-xs">
              <Link
                to="/"
                className="inline-flex items-center gap-1 text-label-md text-accent-cta font-semibold hover:underline min-h-[44px] py-2"
              >
                <FiArrowLeft aria-hidden="true" />
                Return to home
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ForgotPassword;
